/**
 * Maturity Targets API Service (Nurse-Saul + Arrhenius groups)
 *
 * Migrated from the legacy ConXEdge MaturityNurseSaulList / MaturityArrheniusList
 * pages. Temperatures are exchanged in degrees Celsius; the °F companion is a
 * display-only concern handled in the form component.
 */
import { apiClient } from './base';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NurseSaulTarget {
  id: string;
  company_id: string;
  group_id: string;
  group_description?: string | null;
  datum_temp: number;
  /** Locked: the TTF ASTM default, or used by a finished calibration or started pour. */
  is_locked?: boolean;
  lock_reason?: string | null;
  /** The company's seeded TTF (ASTM C1074) default, datum 0 °C. */
  is_default?: boolean;
}

export interface NurseSaulCreate {
  company_id: string;
  group_id: string;
  group_description?: string;
  datum_temp: number;
}

export interface NurseSaulUpdate {
  group_id?: string;
  group_description?: string;
  datum_temp?: number;
}

export interface ArrheniusTarget {
  id: string;
  company_id: string;
  group_id: string;
  group_description?: string | null;
  activation_energy: number;
  specified_temp: number;
  /** Derived: Q = ActivationEnergy / 8.31 (rounded), returned by the API. */
  q: number;
  /** Locked: the TTF ASTM default, or used by a finished calibration or started pour. */
  is_locked?: boolean;
  lock_reason?: string | null;
  /** The company's seeded TTF (ASTM C1074) default, datum 0 °C. */
  is_default?: boolean;
}

export interface ArrheniusCreate {
  company_id: string;
  group_id: string;
  group_description?: string;
  activation_energy: number;
  specified_temp: number;
}

export interface ArrheniusUpdate {
  group_id?: string;
  group_description?: string;
  activation_energy?: number;
  specified_temp?: number;
}

// ---------------------------------------------------------------------------
// Datum temperature / Q-value determination (ASTM C1074-19 Appendix X1)
// ---------------------------------------------------------------------------

export interface DatumQPointInput {
  /** Test age in days (average age of the cubes broken at that age). */
  age_days: number;
  /** Average cube compressive strength at that age. */
  strength: number;
}

export interface DatumQBathInput {
  bath_temp_c: number;
  points: DatumQPointInput[];
}

export interface DatumQAnalyzeRequest {
  baths: DatumQBathInput[];
  strength_unit: string;
}

export interface DatumQWarning {
  code: string;
  severity: 'info' | 'warning' | 'error';
  message: string;
  bath_temp_c?: number | null;
}

export interface DatumQLineFit {
  slope: number;
  intercept: number;
  r2: number;
  n: number;
}

export interface DatumQBathResult {
  bath_temp_c: number;
  bath_temp_k: number;
  inv_temp_k: number;
  n: number;
  su: number;
  k: number;
  ln_k: number;
  t0: number;
  rsd: number;
  r2: number;
  converged: boolean;
  predicted: number[];
}

export interface DatumQAnalyzeResponse {
  strength_unit: string;
  baths: DatumQBathResult[];
  datum_fit: DatumQLineFit | null;
  datum_temp_c: number | null;
  q_fit: DatumQLineFit | null;
  q_value_k: number | null;
  activation_energy_j_mol: number | null;
  warnings: DatumQWarning[];
  kelvin_offset: number;
  gas_constant: number;
}

export interface DatumQExampleResponse {
  source: string;
  strength_unit: string;
  baths: DatumQBathInput[];
  expected_datum_temp_c: number;
  expected_q_value_k: number;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const maturityService = {
  // Nurse-Saul
  listNurseSaul: async (companyId?: string): Promise<NurseSaulTarget[]> => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await apiClient.get('/maturity-targets/nurse-saul', { params });
    return response.data;
  },
  createNurseSaul: async (data: NurseSaulCreate): Promise<NurseSaulTarget> => {
    const response = await apiClient.post('/maturity-targets/nurse-saul', data);
    return response.data;
  },
  updateNurseSaul: async (id: string, data: NurseSaulUpdate): Promise<NurseSaulTarget> => {
    const response = await apiClient.put(`/maturity-targets/nurse-saul/${id}`, data);
    return response.data;
  },
  deleteNurseSaul: async (id: string): Promise<void> => {
    await apiClient.delete(`/maturity-targets/nurse-saul/${id}`);
  },

  // Arrhenius
  listArrhenius: async (companyId?: string): Promise<ArrheniusTarget[]> => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await apiClient.get('/maturity-targets/arrhenius', { params });
    return response.data;
  },
  createArrhenius: async (data: ArrheniusCreate): Promise<ArrheniusTarget> => {
    const response = await apiClient.post('/maturity-targets/arrhenius', data);
    return response.data;
  },
  updateArrhenius: async (id: string, data: ArrheniusUpdate): Promise<ArrheniusTarget> => {
    const response = await apiClient.put(`/maturity-targets/arrhenius/${id}`, data);
    return response.data;
  },
  deleteArrhenius: async (id: string): Promise<void> => {
    await apiClient.delete(`/maturity-targets/arrhenius/${id}`);
  },

  // Datum temperature / Q-value determination (stateless)
  getDatumQExample: async (): Promise<DatumQExampleResponse> => {
    const response = await apiClient.get('/maturity-targets/datum-q/example');
    return response.data;
  },
  analyzeDatumQ: async (data: DatumQAnalyzeRequest): Promise<DatumQAnalyzeResponse> => {
    const response = await apiClient.post('/maturity-targets/datum-q/analyze', data);
    return response.data;
  },
};

export default maturityService;
